/**
 * Approvals → TikTok (Deivydas 2026-10-26): the TikTok Shop chats whose
 * reply Zoye drafted and a person has not sent yet — the approval week of
 * TIKTOK_REPLIES_NEED_APPROVAL. "Waiting on you" lists actions only, so these
 * were findable only as "Drafted" among every other ticket in the inbox.
 *
 * The list on the left, oldest draft first; the ticket pane on the right is
 * the inbox's own, so Send, Edit & send and Hand off work exactly as there.
 * A send or hand-off refreshes every ticket query, and the chat leaves the list.
 */

import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';
import { apiGet } from '../../api/client';
import { Badge, Card, CardHeader } from '../../components/ui/primitives';
import { Pager } from '../../components/ui/ListControls';
import { TicketPane } from '../../components/ticket/TicketPane';
import { ZendeskTicketRef } from '../../components/ticket/ZendeskTicketRef';
import { cn } from '../../lib/cn';
import { formatRelative } from '../../lib/format';
import { toQuery } from '../../lib/filters/list-params';
import { tiktokWaitingKey, type TikTokWaitingResponse } from '../../api/tickets';

export function TikTokQueue({
  brandId,
  page,
  onPage,
}: {
  brandId: string;
  page: number;
  onPage: (next: number) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const query = toQuery({ brandId, page });
  const { data, isLoading } = useQuery({
    queryKey: tiktokWaitingKey(query),
    queryFn: () => apiGet<TikTokWaitingResponse>(`/api/tickets/tiktok-waiting?${query}`),
    placeholderData: keepPreviousData,
  });
  const tickets = useMemo(() => data?.tickets ?? [], [data]);
  // A sent chat leaves the list; the selection falls to the next one.
  const selected = tickets.find((ticket) => ticket.id === selectedId) ?? tickets[0] ?? null;

  if (isLoading) return <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>;

  if (tickets.length === 0) {
    return (
      <Card>
        <CardHeader title="Nothing here" />
        <div className="px-4 py-10 text-center">
          <MessageCircle size={26} strokeWidth={1.5} className="mx-auto mb-3 text-ink-300" aria-hidden />
          <p className="text-sm text-ink-700">No TikTok replies are waiting to be sent.</p>
          <p className="mx-auto mt-2 max-w-[560px] text-sm text-ink-400">
            When a TikTok Shop customer writes, Zoye drafts the reply and it waits here for a person to
            send. Chats she hands off go straight to the CS group in Zendesk and never appear here.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="flex h-[calc(100vh-11rem)] min-h-[480px] overflow-hidden">
      <div className="flex w-80 shrink-0 flex-col border-r border-line bg-surface">
        <ul className="min-h-0 flex-1 overflow-y-auto">
          {tickets.map((ticket) => {
            const run = ticket.runs[0];
            const active = selected?.id === ticket.id;
            const zendeskStatus = ticket.zendesk?.status ?? null;
            return (
              <li key={ticket.id}>
                {/* A div, not a <button>: the ticket number inside links to Zendesk. */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(ticket.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedId(ticket.id);
                    }
                  }}
                  aria-current={active ? 'true' : undefined}
                  className={cn(
                    'block w-full cursor-pointer border-b border-line px-3.5 py-2.5 text-left transition-colors duration-fast',
                    active ? 'bg-accent-50 shadow-[inset_3px_0_0_var(--accent-600)]' : 'hover:bg-surface-sunken',
                  )}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-xs font-semibold text-ink-900">
                      {ticket.customerName ?? ticket.customerEmail ?? 'TikTok customer'}
                    </span>
                    <ZendeskTicketRef externalId={ticket.externalId} className="shrink-0 font-mono text-2xs text-ink-400" />
                  </div>
                  <div className="mt-0.5 line-clamp-2 text-xs text-ink-500">{run?.draftReply ?? ''}</div>
                  <div className="mt-1 flex items-center gap-1.5 text-2xs text-ink-400">
                    <span>{ticket.brand.name}</span>
                    {run ? <span>· drafted {formatRelative(run.startedAt)}</span> : null}
                    {/* Solved or pending in Zendesk: a person may have answered there already. */}
                    {zendeskStatus && zendeskStatus !== 'new' && zendeskStatus !== 'open' ? (
                      <Badge tone="held" className="ml-auto">
                        Zendesk: {zendeskStatus}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        {data && data.total > data.pageSize ? (
          <Pager
            className="border-t border-line px-3 py-2"
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={onPage}
          />
        ) : null}
      </div>
      <div className="min-w-0 flex-1 bg-paper">
        {selected ? <TicketPane key={selected.id} ticketId={selected.id} /> : null}
      </div>
    </Card>
  );
}
